import DeliveryOption from '@/controllers/options/DeliveryOption';
import DeliveryOptionConstants from '@/controllers/options/DeliveryOptionConstants';
import DeliveryOptionType from '@/controllers/options/DeliveryOptionTypeEnum';
import DeliveryOptionsProvider from '@/models/DeliveryOptionsProvider';
import Section from '@/models/Section';
import TestPart from '@/models/TestPart';
import Utility from '@/util/Utility';
import TestController from '../../controllers/TestController';
import CompanionMaterialItemConverter from './companion-material/CompanionMaterialItemConverter';
import DeliveryOptionHandlerFactory from './DeliveryOptionHandlerFactory';

class DeliveryOptionBuilder {
  private readonly FORM_SETTINGS = 'form_settings';
  private readonly SECTION_SETTINGS = 'section_settings';
  private readonly companionMaterialItemConverter: CompanionMaterialItemConverter =
    new CompanionMaterialItemConverter();

  build(testController: TestController, practiceTest = false): void {
    if (!testController || !testController.formController || !testController.formController.formData) {
      return;
    }

    const formSettingsJson = testController.formController?.formData.get(this.FORM_SETTINGS);

    const testPart: TestPart = testController.test!.testParts[0];
    if (formSettingsJson && testController.test && testController.test.testParts.length > 0) {
      const formSettings: Map<string, unknown> = this.createMap(formSettingsJson);
      formSettings.forEach((value: unknown, key: string) => {
        if (key === this.SECTION_SETTINGS) {
          this.handleSectionSettings(formSettings, testPart, testController);
        } else {
          DeliveryOptionHandlerFactory.createDeliveryOptionHandler(key, testController).execute(testPart, formSettings);
        }
      });
    }

    this.handleCompanionMaterialItems(testPart, testController);
    this.handleTextToSpeechAccommodations(testController, practiceTest, testPart);
  }

  private handleCompanionMaterialItems(testPart: TestPart, testController: TestController): void {
    testPart.sections.forEach((section) => {
      section.items.forEach((item) => {
        item.companionMaterialItems.forEach((companionMaterialItem) => {
          const map: Map<string, unknown> = this.companionMaterialItemConverter.build(companionMaterialItem);
          if (map.size === 1) {
            DeliveryOptionHandlerFactory.createDeliveryOptionHandler(Array.from(map.keys())[0], testController).execute(
              item,
              map
            );
          }
        });
      });
    });
  }

  private handleTextToSpeechAccommodations(
    testController: TestController,
    practiceTest: boolean,
    provider: DeliveryOptionsProvider
  ) {
    const textToSpeechEnabled = 'textToSpeechEnabled';
    const textToSpeechGraphicsEnabled = 'textToSpeechGraphicsEnabled';
    const textToSpeechElaEnabled = 'textToSpeechElaEnabled';
    const dynamicTextToSpeechEnabled = 'dynamicTextToSpeechEnabled';

    if (testController!.formController!.formData.get('classroom_test')) {
      if (
        this.textToSpeechAccommodationsEnabled(
          [textToSpeechEnabled, textToSpeechGraphicsEnabled, textToSpeechElaEnabled, dynamicTextToSpeechEnabled],
          testController.jwt
        )
      ) {
        const deliveryOption: DeliveryOption = new DeliveryOption(DeliveryOptionConstants.TEXT_TO_SPEECH);
        deliveryOption.type = DeliveryOptionType.FIXED;
        provider.addDeliveryOption(deliveryOption);
      }
      return;
    }

    const textToSpeechAccommodations: Array<string> | null = testController!.formController!.formData.get(
      'tts_accomms'
    ) as Array<string>;
    if (textToSpeechAccommodations) {
      const previewMode: boolean = Utility.getValueFromJwtWithDefaultValue(testController.jwt, 'previewMode', false);
      const showTextToSpeechInFormPreviewMode: boolean = Utility.getFormPreviewSettingValueFromJWT(
        testController.jwt,
        'show_tts',
        false
      );
      let add = false;
      if (
        ((practiceTest && !previewMode) || (practiceTest && previewMode && showTextToSpeechInFormPreviewMode)) &&
        textToSpeechAccommodations.length > 0
      ) {
        add = true;
      } else if (!testController.isPracticeTest()) {
        for (let i = 0; i < textToSpeechAccommodations.length; i++) {
          switch (textToSpeechAccommodations[i]) {
            case 'tts': {
              add = Utility.getAccommodationFromJWT<boolean>(testController.jwt, textToSpeechEnabled, false);
              break;
            }
            case 'tts_graphics': {
              add = Utility.getAccommodationFromJWT<boolean>(testController.jwt, textToSpeechGraphicsEnabled, false);
              break;
            }
            case 'ttsela': {
              add = Utility.getAccommodationFromJWT<boolean>(testController.jwt, textToSpeechElaEnabled, false);
              break;
            }
            case 'dynamic_tts': {
              add = Utility.getAccommodationFromJWT<boolean>(testController.jwt, dynamicTextToSpeechEnabled, false);
              break;
            }
          }
          if (add) {
            break;
          }
        }
      }

      if (add) {
        const deliveryOption: DeliveryOption = new DeliveryOption(DeliveryOptionConstants.TEXT_TO_SPEECH);
        deliveryOption.type = DeliveryOptionType.FIXED;
        deliveryOption.addSetting('types', textToSpeechAccommodations.toString());
        provider.addDeliveryOption(deliveryOption);
      }
    }
  }

  private textToSpeechAccommodationsEnabled(textToSpeechValueArray: Array<string>, jwt: string | undefined): boolean {
    if (jwt) {
      for (let i = 0; i < textToSpeechValueArray.length; i++) {
        if (Utility.getAccommodationFromJWT<boolean>(jwt, textToSpeechValueArray[i], false)) {
          return true;
        }
      }
    }
    return false;
  }

  private handleSectionSettings(
    formSettings: Map<string, unknown>,
    testPart: TestPart,
    testController: TestController
  ): void {
    this.createSectionArray(formSettings.get('section_settings') as Array<unknown>).forEach((sectionMap) => {
      const identifier: string = sectionMap.get('identifier') as string;
      if (identifier) {
        const section: Section | undefined = testPart.sections.find((section) => section.identifier === identifier);
        if (section) {
          sectionMap.forEach((sectionValue: unknown, sectionKey: string) => {
            DeliveryOptionHandlerFactory.createDeliveryOptionHandler(sectionKey, testController).execute(
              section,
              sectionMap
            );
          });
        }
      }
    });
  }
  private createSectionArray(array: Array<unknown>): Array<Map<string, unknown>> {
    const sectionMaps: Array<Map<string, unknown>> = new Array<Map<string, unknown>>();
    if (array) {
      for (let i = 0; i < array.length; i++) {
        sectionMaps.push(this.createMap(array[i]));
      }
    }

    return sectionMaps;
  }

  private createMap(formSettingJson): Map<string, unknown> {
    const map: Map<string, unknown> = new Map();
    for (const key of Object.keys(formSettingJson)) {
      map.set(key, formSettingJson[key]);
    }
    return map;
  }
}

export default DeliveryOptionBuilder;
